import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const fmt = (cents) =>
  '$' + Math.abs(Number(cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const QUICK_LINKS = [
  { label: 'Wire Requests',  path: '/admin/finance/wire-requests', desc: 'Approve pending wire transfers' },
  { label: 'P&L',            path: '/admin/finance/pl',            desc: 'Profit and loss statement' },
  { label: 'Reconciliation', path: '/admin/finance/reconciliation', desc: 'Match transactions to loans' },
  { label: 'Partner Fees',   path: '/admin/finance/partner-fees',  desc: 'Referral commission payments' },
  { label: 'Recipients',     path: '/admin/finance/recipients',    desc: 'Approved payment recipients' },
  { label: 'Spending',       path: '/admin/finance/spending',      desc: 'Operating expense analysis' },
  { label: 'Acquisition',    path: '/admin/finance/acquisition',   desc: 'A-B credit facility' },
  { label: 'Warehouse',      path: '/admin/finance/warehouse',     desc: 'Warehouse lending lines' },
];

export default function AdminFinance() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading]   = useState(true);

  const headers = { Authorization: `Bearer ${localStorage.getItem('doorly_admin_token')}` };

  useEffect(() => {
    const fetchOverview = async () => {
      try {
        // All-settled so one failing endpoint doesn't blank the whole dashboard.
        const [wiresRes, feesRes, facilitiesRes, plRes] = await Promise.allSettled([
          axios.get(`${API}/api/admin/agents/finance/wire-requests`, { headers }),
          axios.get(`${API}/api/admin/agents/finance/partner-fees`, { headers }),
          axios.get(`${API}/api/admin/credit-facilities`, { headers }),
          axios.get(`${API}/api/admin/agents/finance/pl?preset=this_month`, { headers }),
        ]);
        const wires = wiresRes.status === 'fulfilled' ? wiresRes.value.data : [];
        const fees  = feesRes.status  === 'fulfilled' ? feesRes.value.data  : [];
        const facilities = facilitiesRes.status === 'fulfilled' ? facilitiesRes.value.data : [];
        const pl    = plRes.status    === 'fulfilled' ? plRes.value.data    : null;

        const pendingWires = wires.filter(w => w.status === 'pending_approval');
        const pendingFees  = fees.filter(f => f.status === 'pending');
        const warehouseFacilities = facilities.filter(f => f.facility_type === 'warehouse');
        const acquisitionFacility = facilities.find(f => f.facility_type === 'acquisition');

        const totalWarehouseLimit = warehouseFacilities.reduce(
          (s, f) => s + parseInt(f.credit_limit_cents || 0), 0);
        const totalWarehouseDrawn = warehouseFacilities.reduce(
          (s, f) => s + parseInt(f.balance_cents || 0), 0);

        setOverview({
          pendingWires, pendingFees,
          warehouseFacilities, acquisitionFacility,
          totalWarehouseLimit, totalWarehouseDrawn,
          warehouseUtilization: totalWarehouseLimit > 0
            ? Math.round((totalWarehouseDrawn / totalWarehouseLimit) * 10000) / 100
            : 0,
          pl,
        });
      } catch (err) {
        console.error('Finance overview failed:', err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchOverview();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AdminLayout>
      <div style={{ padding: '24px 32px' }}>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>Finance</h1>
          <p style={{ fontSize: 13, color: '#6b7280', marginTop: 4, marginBottom: 0 }}>
            Overview of financial activity
          </p>
        </div>

        {loading && <div style={{ color: '#6b7280', fontSize: 13, padding: 20 }}>Loading…</div>}

        {!loading && overview && (
          <>
            {/* Summary metric cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
              {[
                {
                  label: 'Pending Wire Approvals',
                  value: overview.pendingWires.length,
                  alert: overview.pendingWires.length > 0,
                  path: '/admin/finance/wire-requests',
                },
                {
                  label: 'Partner Fees Due',
                  value: overview.pendingFees.length,
                  alert: overview.pendingFees.length > 0,
                  path: '/admin/finance/partner-fees',
                },
                {
                  label: 'Warehouse Utilization',
                  value: `${overview.warehouseUtilization}%`,
                  alert: overview.warehouseUtilization > 80,
                  path: '/admin/finance/warehouse',
                },
                {
                  label: 'Net Income (This Month)',
                  value: overview.pl ? fmt(overview.pl.totals.netIncome) : '—',
                  alert: overview.pl ? overview.pl.totals.netIncome < 0 : false,
                  path: '/admin/finance/pl',
                },
              ].map(card => (
                <div key={card.label}
                  onClick={() => navigate(card.path)}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
                  style={{
                    background: '#fff',
                    border: `1px solid ${card.alert ? '#fca5a5' : '#e5e7eb'}`,
                    borderRadius: 12, padding: '16px 18px', cursor: 'pointer',
                  }}>
                  <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                    {card.label}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: card.alert ? '#991b1b' : '#111827' }}>
                    {card.value}
                  </div>
                </div>
              ))}
            </div>

            {/* Pending wires preview */}
            {overview.pendingWires.length > 0 && (
              <div style={{ background: '#fff', border: '1px solid #fca5a5', borderRadius: 12, padding: '16px 20px', marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>Pending Wire Approvals</div>
                  <button onClick={() => navigate('/admin/finance/wire-requests')}
                    style={{ background: 'none', border: 'none', color: '#3a6ad9', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    View all →
                  </button>
                </div>
                {overview.pendingWires.slice(0, 3).map(w => (
                  <div key={w.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '8px 0', borderTop: '1px solid #f3f4f6', fontSize: 12,
                  }}>
                    <div style={{ color: '#374151' }}>
                      {w.recipient_name}
                      {w.memo && <span style={{ color: '#9ca3af', marginLeft: 8 }}>{w.memo}</span>}
                    </div>
                    <div style={{ fontWeight: 700, color: '#111827' }}>{fmt(w.amount_cents)}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Warehouse utilization */}
            {overview.warehouseFacilities.length > 0 && (
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 20px', marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>Warehouse Lines</div>
                  <button onClick={() => navigate('/admin/finance/warehouse')}
                    style={{ background: 'none', border: 'none', color: '#3a6ad9', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    Manage →
                  </button>
                </div>
                {overview.warehouseFacilities.map(f => (
                  <div key={f.id} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                      <span style={{ color: '#374151', fontWeight: 600 }}>{f.lender_name}</span>
                      <span style={{ color: '#6b7280' }}>
                        {fmt(f.balance_cents)} / {fmt(f.credit_limit_cents)} ·{' '}
                        <strong style={{ color: '#111827' }}>{f.utilization_pct}%</strong>
                      </span>
                    </div>
                    <div style={{ background: '#f3f4f6', borderRadius: 999, height: 6, overflow: 'hidden' }}>
                      <div style={{
                        width: `${Math.min(f.utilization_pct, 100)}%`,
                        height: '100%',
                        background: f.utilization_pct > 80 ? '#ff082f' : f.utilization_pct > 60 ? '#f59e0b' : '#3a6ad9',
                        borderRadius: 999,
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Partner fees pending */}
            {overview.pendingFees.length > 0 && (
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 20px', marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>Partner Fees Pending</div>
                  <button onClick={() => navigate('/admin/finance/partner-fees')}
                    style={{ background: 'none', border: 'none', color: '#3a6ad9', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    View all →
                  </button>
                </div>
                {overview.pendingFees.slice(0, 3).map(f => {
                  const dueDate = f.due_date ? new Date(f.due_date) : null;
                  const daysUntilDue = dueDate ? Math.ceil((dueDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
                  return (
                    <div key={f.id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '8px 0', borderTop: '1px solid #f3f4f6', fontSize: 12,
                    }}>
                      <div style={{ color: '#374151' }}>
                        {f.ach_account_holder_name || 'Partner'}
                        <span style={{ color: '#9ca3af', marginLeft: 8 }}>
                          {f.partner_type === 'loan_officer' ? 'LO' : 'Realtor'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        {daysUntilDue !== null && (
                          <span style={{
                            fontSize: 10, fontWeight: 600,
                            color: daysUntilDue < 0 ? '#991b1b' : daysUntilDue <= 7 ? '#854d0e' : '#6b7280',
                          }}>
                            {daysUntilDue < 0 ? `Overdue ${Math.abs(daysUntilDue)}d` : `Due in ${daysUntilDue}d`}
                          </span>
                        )}
                        <span style={{ fontWeight: 700, color: '#111827' }}>
                          ${parseFloat(f.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Quick links grid — every page in the Finance hub. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
              {QUICK_LINKS.map(link => (
                <div key={link.path}
                  onClick={() => navigate(link.path)}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
                  style={{
                    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
                    padding: '14px 16px', cursor: 'pointer',
                  }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 3 }}>{link.label}</div>
                  <div style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.4 }}>{link.desc}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
